import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import {
  ICliente,
  IGeoJSONCircle,
  IGeoJSONMultiPolygon,
  IGeoJSONPolygon,
  IQueryParam,
  IUbicacion,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { DateRangeValue } from '../../../../../componentes/date-picker/date-picker.component';
import { EventosGenericosHttpService } from '../../../../../auxiliares/servicios/http/eventos-genericos.service';
import {
  IOpcionesExportRecorrido,
  IOpcionesExportReportesVehiculo,
  ReportesGenericosService,
} from '../../../../../auxiliares/servicios/http/reportes-genericos.service';
import { DescargasService } from '../../../../navigation/descargas/service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { format } from 'date-fns';
import { BusquedaZonaService } from '../../../vehiculos/contenedor-mapa/barra-lateral/busqueda-especifica/busqueda-zona/busqueda-zona.service';
import { MapaFacilService } from '../../../../../auxiliares/mapa-facil/mapa-facil.service';
import {
  EditingConfig,
  MarkerMapa,
  PolygonMapa,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';

const MAX_DIAS_RANGO = 31;
const MAX_VEHICULOS_HIJOS = 300;

type VehiculoItem = {
  _id: string;
  idCliente: string;
  idsAncestros?: string[];
  identificacion?: string;
  vehiculo?: { patente?: string };
};

@Component({
  selector: 'app-exportaciones-vehiculos',
  templateUrl: './exportaciones-vehiculos.component.html',
  styleUrl: './exportaciones-vehiculos.component.scss',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class ExportacionesVehiculosComponent implements OnInit, OnDestroy {
  private eventosService = inject(EventosGenericosHttpService);
  private reportesService = inject(ReportesGenericosService);
  private descargasService = inject(DescargasService);
  private helper = inject(HelperService);
  private dialog = inject(DialogService);
  private lista2 = inject(Lista2Service);
  private busquedaZonaService = inject(BusquedaZonaService);
  private mapaFacilService = inject(MapaFacilService);

  public tipoExport: 'eventos' | 'reportes' | 'recorridos' | 'zona' = 'eventos';
  public cliente: ICliente | null = null;
  public incluirHijos = true;
  public vehiculo: VehiculoItem | null = null;
  public rango: DateRangeValue | null = null;
  public exportando = false;
  public incluirParadas = false;
  public velocidadMaxima: number | null = null;
  public recorridoMostrarEventos = false;
  public recorridoTiempoDetenido: number | null = null;
  public recorridoVelocidadExceso: number | null = null;
  public totalVehiculosConHijos: number | null = null;
  public cargandoVehiculos = true;

  // Zona
  public zonaMode: 'vehiculosPorZona' | 'zonasPorVehiculo' = 'vehiculosPorZona';
  public zonaTipo: 'dibujo' | 'ubicacion' = 'dibujo';
  public zonaCirculoCenter: [number, number] | null = null;
  public zonaCirculoRadioKm: number | null = null;
  public zonaPoligono: [number, number][][][] | null = null;
  public zonaUbicacion: IUbicacion | null = null;
  public zonaUbicaciones: IUbicacion[] = [];
  public cargandoUbicaciones = false;
  public zonaTiempoMinimo = 5;
  public readonly zonaEditingConfig: EditingConfig = {
    enabled: true,
    drawingTool: 'Circle',
    allowModify: true,
    allowDelete: true,
    allowedTools: ['Circle', 'Polygon'],
  };
  public readonly zonaUbicacionDisplayConfig: EditingConfig = {
    enabled: false,
  };

  private circleSub?: Subscription;
  private polygonSub?: Subscription;

  public todosLosVehiculos: VehiculoItem[] = [];

  public get vehiculosFiltrados(): VehiculoItem[] {
    if (!this.cliente) return this.todosLosVehiculos;
    return this.todosLosVehiculos.filter(
      (v) =>
        v.idCliente === this.cliente!._id ||
        v.idsAncestros?.includes(this.cliente!._id),
    );
  }

  public get hijosDeshabilitado(): boolean {
    return (
      this.totalVehiculosConHijos !== null &&
      this.totalVehiculosConHijos > MAX_VEHICULOS_HIJOS
    );
  }

  public get rangoExcedeMes(): boolean {
    if (!this.rango?.start || !this.rango?.end) return false;
    const diff =
      new Date(this.rango.end).getTime() - new Date(this.rango.start).getTime();
    return diff > MAX_DIAS_RANGO * 24 * 60 * 60 * 1000;
  }

  async ngOnInit(): Promise<void> {
    this.mapaFacilService.circleSubject.next(undefined);
    this.mapaFacilService.multiPolygonSubject.next(undefined);
    this.circleSub = this.mapaFacilService.circle.subscribe((c) => {
      if (c?.center) this.zonaCirculoCenter = c.center;
      this.zonaCirculoRadioKm = c?.radius ?? null;
      if (c) this.zonaPoligono = null;
    });
    this.polygonSub = this.mapaFacilService.multiPolygon.subscribe((coords) => {
      this.zonaPoligono = (coords as [number, number][][][]) ?? null;
      if (coords) {
        this.zonaCirculoCenter = null;
        this.zonaCirculoRadioKm = null;
      }
    });
    try {
      const res = await firstValueFrom(
        this.lista2.vehiculos(
          {
            select:
              '_id idCliente idsAncestros identificacion vehiculo.patente',
            includeChildren: true,
            unlimited: true,
          },
          0,
        ),
      );
      this.todosLosVehiculos = res.datos as VehiculoItem[];
    } catch {
      this.todosLosVehiculos = [];
    } finally {
      this.cargandoVehiculos = false;
    }
  }

  ngOnDestroy(): void {
    this.circleSub?.unsubscribe();
    this.polygonSub?.unsubscribe();
    this.mapaFacilService.circleSubject.next(undefined);
    this.mapaFacilService.multiPolygonSubject.next(undefined);
  }

  /** Subtítulo del encabezado según el tipo de exportación y la selección. */
  public get subtitulo(): string {
    if (this.tipoExport === 'recorridos') {
      return this.vehiculo
        ? 'Exportará el recorrido del vehículo seleccionado'
        : 'Recorridos requiere seleccionar un vehículo';
    }
    if (this.tipoExport === 'zona') {
      if (this.zonaMode === 'zonasPorVehiculo') {
        return this.vehiculo
          ? 'Exportará las zonas visitadas por el vehículo seleccionado'
          : 'Zonas por vehículo requiere seleccionar un vehículo';
      }
      return 'Exportará qué vehículos pasaron por la zona definida';
    }
    const que = this.tipoExport === 'reportes' ? 'reportes' : 'eventos';
    if (this.vehiculo) {
      return que === 'reportes'
        ? 'Exportará los reportes del vehículo seleccionado'
        : 'Exportará sólo los eventos del vehículo seleccionado';
    }
    if (this.cliente) {
      return `Exportará todos los ${que} de vehículos del cliente`;
    }
    return 'Seleccioná un vehículo o un cliente para comenzar';
  }

  public get exportLabel(): string {
    if (this.tipoExport === 'reportes') {
      return this.vehiculo
        ? 'Exportar reportes del vehículo'
        : 'Exportar reportes del cliente';
    }
    if (this.tipoExport === 'recorridos') {
      return 'Exportar recorrido del vehículo';
    }
    if (this.tipoExport === 'zona') {
      return this.zonaMode === 'vehiculosPorZona'
        ? 'Exportar vehículos por zona'
        : 'Exportar zonas del vehículo';
    }
    return this.vehiculo
      ? 'Exportar eventos del vehículo'
      : 'Exportar eventos del cliente';
  }

  public get canExport(): boolean {
    let tieneEntidad: boolean;
    if (this.tipoExport === 'recorridos') {
      tieneEntidad = !!this.vehiculo;
    } else if (this.tipoExport === 'zona') {
      tieneEntidad =
        this.zonaMode === 'vehiculosPorZona'
          ? this.zonaTipo === 'dibujo'
            ? !!this.zonaCirculoCenter || !!this.zonaPoligono
            : !!this.zonaUbicacion
          : !!this.vehiculo;
    } else {
      tieneEntidad = !!this.cliente || !!this.vehiculo;
    }
    return (
      tieneEntidad &&
      !!this.rango?.start &&
      !!this.rango?.end &&
      !this.exportando &&
      !this.rangoExcedeMes
    );
  }

  public onZonaTipoChange(tipo: 'dibujo' | 'ubicacion'): void {
    this.zonaCirculoCenter = null;
    this.zonaCirculoRadioKm = null;
    this.zonaPoligono = null;
    this.mapaFacilService.circleSubject.next(undefined);
    this.mapaFacilService.multiPolygonSubject.next(undefined);
    this.zonaUbicacion = null;
    if (tipo === 'ubicacion') this.cargarUbicaciones();
  }

  public onZonaUbicacionChange(u: IUbicacion | null): void {
    this.zonaUbicacion = u;
    if (!u?.geojson) {
      this.mapaFacilService.updateData({});
      return;
    }
    const geo = u.geojson;
    if (geo.type === 'Polygon') {
      const ring = (geo as IGeoJSONPolygon).coordinates[0];
      const centerLng = ring.reduce((s, c) => s + c[0], 0) / ring.length;
      const centerLat = ring.reduce((s, c) => s + c[1], 0) / ring.length;
      const polygon: PolygonMapa = {
        points: (geo as IGeoJSONPolygon).coordinates as any,
      };
      this.mapaFacilService.updateData({
        polygons: [polygon],
        center: [centerLng, centerLat],
      });
    } else if (geo.type === 'MultiPolygon') {
      const ring = (geo as IGeoJSONMultiPolygon).coordinates[0][0];
      const centerLng =
        ring.reduce((s: number, c: number[]) => s + c[0], 0) / ring.length;
      const centerLat =
        ring.reduce((s: number, c: number[]) => s + c[1], 0) / ring.length;
      const polygons: PolygonMapa[] = (
        geo as IGeoJSONMultiPolygon
      ).coordinates.map((rings) => ({ points: rings as any }));
      this.mapaFacilService.updateData({
        polygons,
        center: [centerLng, centerLat],
      });
    } else if (geo.type === 'Point') {
      const [lng, lat] = geo.coordinates;
      if ('radius' in geo && (geo as IGeoJSONCircle).radius) {
        this.mapaFacilService.updateData({
          circles: [
            { center: [lng, lat], radius: (geo as IGeoJSONCircle).radius },
          ],
          center: [lng, lat],
        });
      } else {
        const marker: MarkerMapa = {
          coor: [lng, lat],
          label: 'puntoSimple',
          centrar: true,
        };
        this.mapaFacilService.updateData({ markers: [marker] });
      }
    }
  }

  public async cargarUbicaciones(): Promise<void> {
    if (this.zonaUbicaciones.length) return;
    this.cargandoUbicaciones = true;
    try {
      const res = await firstValueFrom(
        this.lista2.ubicacions(
          {
            filter: JSON.stringify({ categoria: 'Vehiculos' }),
            includeChildren: true,
            unlimited: true,
          },
          0,
        ),
      );
      this.zonaUbicaciones = res.datos as IUbicacion[];
    } catch {
      this.zonaUbicaciones = [];
    } finally {
      this.cargandoUbicaciones = false;
    }
  }

  public vehiculoLabel(v: VehiculoItem): string {
    const partes = [v.vehiculo?.patente, v.identificacion].filter(Boolean);
    return partes.join(' - ') || v._id;
  }

  public vehiculoSearchFn(term: string, v: VehiculoItem): boolean {
    const t = term.toLowerCase();
    return (
      (v.vehiculo?.patente?.toLowerCase().includes(t) ?? false) ||
      (v.identificacion?.toLowerCase().includes(t) ?? false)
    );
  }

  public onClienteChange(c: ICliente | null): void {
    this.cliente = c;
    this.vehiculo = null;
    if (!c) {
      this.totalVehiculosConHijos = null;
      this.incluirHijos = true;
      return;
    }
    const count = this.todosLosVehiculos.filter(
      (v) => v.idCliente === c._id || v.idsAncestros?.includes(c._id),
    ).length;
    this.totalVehiculosConHijos = count;
    if (this.hijosDeshabilitado) this.incluirHijos = false;
  }

  public setRangoPreset(
    preset: 'esteMes' | 'mesPasado' | '1semana' | '48h' | '24h',
  ): void {
    const ahora = new Date();
    let start: Date;
    let end: Date = ahora;

    switch (preset) {
      case 'esteMes':
        start = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        break;
      case 'mesPasado':
        start = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
        end = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        break;
      case '1semana':
        start = new Date(ahora.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '48h':
        start = new Date(ahora.getTime() - 48 * 60 * 60 * 1000);
        break;
      case '24h':
        start = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);
        break;
    }

    this.rango = { start: start!.toISOString(), end: end.toISOString() };
  }

  private buildConfirmMessage(): string {
    const desde = format(new Date(this.rango!.start), 'dd/MM/yyyy HH:mm');
    const hasta = format(new Date(this.rango!.end), 'dd/MM/yyyy HH:mm');

    const rows: { label: string; value: string }[] = [];

    if (this.vehiculo) {
      rows.push({
        label: 'Vehículo',
        value: this.vehiculoLabel(this.vehiculo),
      });
    }
    if (this.cliente) {
      rows.push({ label: 'Cliente', value: this.cliente.nombre });
      if (!this.vehiculo) {
        rows.push({
          label: 'Clientes hijos',
          value: this.incluirHijos ? 'Incluidos' : 'No incluidos',
        });
      }
    }

    rows.push({ label: 'Desde', value: desde });
    rows.push({ label: 'Hasta', value: hasta });

    const tableRows = rows
      .map(
        (r) =>
          `<tr>
            <td style="padding:4px 12px 4px 0;color:var(--text-secondary-color);white-space:nowrap">${r.label}</td>
            <td style="padding:4px 0;font-weight:500">${escaparHtml(r.value)}</td>
          </tr>`,
      )
      .join('');

    return `<table style="border-collapse:collapse;margin-top:8px">${tableRows}</table>`;
  }

  public async exportar(): Promise<void> {
    if (!this.canExport) return;

    const title = this.exportLabel;
    const confirmed = await this.dialog.confirm(
      title,
      this.buildConfirmMessage(),
      'Exportar',
      'Cancelar',
    );
    if (!confirmed) return;

    let nombre: string;
    let query: IQueryParam;

    if (this.tipoExport === 'zona') {
      if (this.zonaMode === 'vehiculosPorZona') {
        let geoWithin: any;
        let nombreZona: string;
        if (this.zonaTipo === 'dibujo') {
          if (this.zonaCirculoCenter) {
            geoWithin = {
              $centerSphere: [
                this.zonaCirculoCenter,
                this.zonaCirculoRadioKm! / 6371,
              ],
            };
            nombreZona = `círculo r${Math.round(this.zonaCirculoRadioKm! * 1000)}m`;
          } else {
            geoWithin = {
              $geometry: {
                type: 'Polygon',
                coordinates: this.zonaPoligono![0],
              },
            };
            nombreZona = 'polígono dibujado';
          }
        } else {
          const geo = this.zonaUbicacion!.geojson;
          const radioM = (geo as IGeoJSONCircle).radius;
          // Zona circular (Point + radius en metros): $geometry no admite
          // radio, se consulta con $centerSphere como el círculo dibujado.
          geoWithin =
            geo.type === 'Point' && radioM
              ? { $centerSphere: [geo.coordinates, radioM / 1000 / 6371] }
              : { $geometry: geo };
          nombreZona = this.zonaUbicacion!.identificacion || 'zona';
        }
        nombre = `Vehículos por zona - ${nombreZona}`;
        const rango = this.rango!;
        const minutosMinimos = this.zonaTiempoMinimo;
        // Async: el worker de api-datos corre la geo-query, iterator5000 y el
        // Excel (donde viven los reportes). Crea el job → devuelve jobId al
        // instante y pollea. Evita el timeout de Cloudflare (100s) y saca el
        // cómputo del browser.
        this.descargasService.agregarDescargaJob(nombre, {
          crearJob: () =>
            this.reportesService.crearVehiculosPorZonaJob({
              geoWithin,
              rango: { $gte: rango.start, $lte: rango.end },
              clienteId: this.cliente?._id,
              includeChildren: this.cliente ? this.incluirHijos : true,
              vehiculoId: this.vehiculo?._id,
              minutosMinimos,
              nombreZona,
            }),
          consultarEstado: (jobId) =>
            this.reportesService.getRecorridoJob(jobId),
        });
      } else {
        nombre = `Zonas del vehículo - ${this.vehiculoLabel(this.vehiculo!)}`;
        const vehiculoId = this.vehiculo!._id;
        const rango = this.rango!;
        const minutosMinimos = this.zonaTiempoMinimo;
        this.descargasService.agregarDescargaExcel(nombre, async () => {
          const [reportesRes, ubicacionesRes] = await Promise.all([
            this.reportesService
              .buscarReportesPorVehiculo(vehiculoId, {
                $gte: rango.start,
                $lte: rango.end,
              })
              .then((datos) => ({ datos })),
            firstValueFrom(
              this.lista2.ubicacions(
                {
                  filter: JSON.stringify({ categoria: 'Vehiculos' }),
                  includeChildren: true,
                  unlimited: true,
                },
                0,
              ),
            ),
          ]);
          const zonasPaso = this.busquedaZonaService.procesarReportesParaZonas(
            reportesRes.datos as any,
            ubicacionesRes.datos as IUbicacion[],
            minutosMinimos,
          );
          const idsClientesZonas = [
            ...new Set(
              zonasPaso
                .map((z) => z.ubicacion?.idCliente?.toString())
                .filter(Boolean),
            ),
          ];
          let clientesMapZonas: Map<string, string> | undefined;
          if (idsClientesZonas.length) {
            const clientesRes = await firstValueFrom(
              this.lista2.clientes(
                {
                  filter: JSON.stringify({ _id: { $in: idsClientesZonas } }),
                  unlimited: true,
                  includeChildren: true,
                },
                0,
              ),
            );
            clientesMapZonas = new Map(
              clientesRes.datos.map((c) => [c._id?.toString(), c.nombre]),
            );
          }
          return this.busquedaZonaService.generarZonasPorVehiculoBlobFromData(
            zonasPaso,
            clientesMapZonas,
          );
        });
      }
      return;
    }

    if (this.tipoExport === 'recorridos') {
      const recorridoQuery: IQueryParam = {
        filter: JSON.stringify({
          tipoEntidad: 'Vehiculo',
          idsAsignados: this.vehiculo!._id,
          'valores.timestamp': {
            $gte: this.rango!.start,
            $lte: this.rango!.end,
          },
        }),
      };
      const opciones: IOpcionesExportRecorrido = {
        mostrarEventos: this.recorridoMostrarEventos,
        tiempoDetenidoMinutos: this.recorridoTiempoDetenido || null,
        velocidadExceso: this.recorridoVelocidadExceso || null,
      };
      const nombre = `Recorrido - ${this.vehiculoLabel(this.vehiculo!)}`;
      // Siempre asíncrono: crea el job (devuelve al instante) y pollea el estado.
      // Evita el timeout de Cloudflare (100s) en rangos grandes.
      this.descargasService.agregarDescargaJob(nombre, {
        crearJob: () =>
          this.reportesService.crearRecorridoJob(recorridoQuery, opciones),
        consultarEstado: (jobId) => this.reportesService.getRecorridoJob(jobId),
      });
      return;
    }

    if (this.vehiculo) {
      query = {
        filter: JSON.stringify({
          idEntidad: this.vehiculo._id,
          fechaCreacion: { $gte: this.rango!.start, $lte: this.rango!.end },
        }),
        sort: '-fechaCreacion',
      };
      if (this.tipoExport === 'reportes') {
        const opciones: IOpcionesExportReportesVehiculo = {
          incluirParadas: this.incluirParadas || undefined,
          velocidadMaxima: this.velocidadMaxima || undefined,
        };
        nombre = `Reportes vehículo - ${this.vehiculoLabel(this.vehiculo)}`;
        // Async: el worker de api-datos corre la query y arma el Excel. Crea el
        // job → devuelve jobId al instante y pollea. Evita el timeout de
        // Cloudflare (100s) y saca el cómputo del browser.
        const queryReportesVehiculo = query;
        this.descargasService.agregarDescargaJob(nombre, {
          crearJob: () =>
            this.reportesService.crearReportesVehiculoJob(
              queryReportesVehiculo,
              opciones,
              `${nombre}.xlsx`,
            ),
          consultarEstado: (jobId) =>
            this.reportesService.getRecorridoJob(jobId),
        });
      } else {
        nombre = `Eventos vehículo - ${this.vehiculoLabel(this.vehiculo)}`;
        this.descargasService.agregarDescargaExcel(nombre, () =>
          this.eventosService.exportarVehiculoIndividualComoBlob(query),
        );
      }
    } else {
      query = {
        filter: JSON.stringify({
          idCliente: this.cliente!._id,
          fechaCreacion: { $gte: this.rango!.start, $lte: this.rango!.end },
        }),
        includeChildren: this.incluirHijos,
        sort: '-fechaCreacion',
      };
      if (this.tipoExport === 'reportes') {
        const opciones: IOpcionesExportReportesVehiculo = {
          incluirParadas: this.incluirParadas || undefined,
          velocidadMaxima: this.velocidadMaxima || undefined,
        };
        nombre = `Reportes vehículos - ${this.cliente!.nombre}`;
        // Async: el worker de api-datos corre la query pesada (todos los vehículos
        // del cliente) y arma el Excel. Crea el job → devuelve jobId al instante y
        // pollea. Evita el timeout de Cloudflare (100s), que colgaba este export.
        const queryReportesCliente = query;
        this.descargasService.agregarDescargaJob(nombre, {
          crearJob: () =>
            this.reportesService.crearReportesClienteJob(
              queryReportesCliente,
              opciones,
              `${nombre}.xlsx`,
            ),
          consultarEstado: (jobId) =>
            this.reportesService.getRecorridoJob(jobId),
        });
      } else {
        nombre = `Eventos vehículos - ${this.cliente!.nombre}`;
        this.descargasService.agregarDescargaExcel(nombre, () =>
          this.eventosService.exportarVehiculosClienteComoBlob(query),
        );
      }
    }

    this.helper.notifSuccess('Exportación iniciada — ver panel de Descargas');
  }
}
